/* eslint-disable no-console */
import React from 'react';
import PropTypes from 'prop-types';

/**
 * Computes the new selection state for all pills based on the clicked pill and selection mode.
 *
 * @param {Object} previousState - The current state of all pills in the format:
 * { 'pill-1': boolean, 'pill-2': boolean, ... }
 * @param {number} index - The zero-based index of the clicked pill
 * @param {('single'|'multi')} mode - The selection mode of the pill group
 *
 * @returns {Object} New state object for all pills
 *
 * @description
 * - In 'single' mode: Deselects all other pills and toggles the clicked pill
 * - In 'multi' mode: Only toggles the clicked pill, preserving all other pill states
 */
const computeState = (previousState, index, mode) => {
  const id = index + 1;
  const currentProp = `pill-${id}`;

  if (mode === 'single') {
    const newState = {};

    Object.keys(previousState).forEach((key) => {
      if (key !== currentProp) {
        newState[key] = false;
      }
    });

    newState[currentProp] = !previousState[currentProp]?.selected;

    return newState;
  }
  return {
    ...previousState,
    [currentProp]: !previousState[currentProp]?.selected
  };
};

/**
 * Resets all pills to their initial unselected state.
 *
 * @param {Object} previousState - The current state of all pills in the format:
 * { 'pill-1': boolean, 'pill-2': boolean, ... }
 * @returns {Object} New state object with all pills set to false (unselected)
 *
 * @description
 * This function is used when the selection mode changes to clear any existing selections
 * and ensure all pills start in a clean, unselected state.
 */
const resetState = (previousState) => {
  const newState = {};

  Object.keys(previousState).forEach((key) => {
    newState[key] = false;
  });

  return newState;
};

/**
 * A container component that manages a group of Pills with coordinated selection behavior.
 *
 * PillGroup provides centralized state management for multiple Pill components, supporting
 * both single and multiple selection modes. It automatically handles the selection logic
 * and passes the appropriate props to child Pills.
 *
 * @component
 * @param {Object} props - The component props
 * @param {React.ReactNode} props.children - Pill components to be managed by the group
 * @param {('single'|'multi')} [props.selection='multi'] - Selection mode for the pills
 * @param {function} [props.onChange] - Callback fired when pill selection changes
 *
 * @example
 * // Single selection mode
 * import { Pill, PillGroup } from '@one-thd/sui-atomic-components;
 *
 * <PillGroup selection="single" onChange={handleChange}>
 *   <Pill label="Option 1" />
 *   <Pill label="Option 2" />
 * </PillGroup>
 *
 * @example
 * // Multiple selection mode (default)
 * import { Pill, PillGroup } from '@one-thd/sui-atomic-components;
 *
 * <PillGroup selection="multi">
 *   <Pill label="Filter 1" />
 *   <Pill label="Filter 2" />
 *   <Pill label="Filter 3" />
 * </PillGroup>
 */
const PillGroup = (props) => {
  const { children, selection = 'multi', onChange } = props;
  const [pills, setPills] = React.useState({});

  const computeChildren = React.useCallback(() => {
    const newPills = {};
    React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return;

      return child?.props?.selected || false;
    }).forEach((child, index) => Object.assign(newPills, { [`pill-${index + 1}`]: child }));
    setPills(newPills);
  }, [children]);

  const handleChange = (event, index) => {
    setPills((prev) => computeState(prev, index, selection));

    if (onChange) {
      onChange(event);
    }
  };

  React.useEffect(() => {
    computeChildren();
  }, [computeChildren]);

  React.useEffect(() => {
    setPills((prev) => resetState(prev));
  }, [selection]);

  return (
    <>
      { React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return;

        return React.cloneElement(child, {
          selected: pills[`pill-${index + 1}`],
          selectable: true,
          onClick: (event) => {
            handleChange(event, index);
            if (child?.props?.onClick) {
              child?.props?.onClick();
            }
          }
        });
      })}
    </>
  );
};

PillGroup.displayName = 'PillGroup';

PillGroup.propTypes = {
  /**
   * The content of the component, typically Pill components.
   */
  children: PropTypes.node,
  /**
   * Defines the selection behavior for pills within the group.
   * - 'single': Only one pill can be selected at a time
   * - 'multi': Multiple pills can be selected simultaneously
   * @default 'multi'
   */
  selection: PropTypes.oneOf(['single', 'multi']),
  /**
   * Callback fired when the selection state of any pill in the group changes.
   */
  onChange: PropTypes.func,

};

export { PillGroup };